// Sourced from ExternalStatusReason1Code - https://github.com/narmi/fednow/blob/main/reference/schemas/external_code_sets/3Q2023_ExternalCodeSets.json
export const CANCELLATION_CODES = {
  AGNT: "Agent in the payment workflow is incorrect.",
  AM09: "Amount is not the amount agreed or expected.",
  WNTB: "Breach of warranty provided in connection with a request for payment.",
  ENUE: "Cancellation or request for return requested by the Debtor specifically due to one or more errors by debtor in the original Credit Transfer. Usage: This code can be used for any error in the original Credit Transfer made by the Debtor. Can also be used if multiple errors were made in the original Credit Transfer.",
  CUTA: "Cancellation requested because an investigation request has been received and no remediation is possible.",
  DS24: "Cancellation requested because the original payment order expired due to time-out.",
  SYAD: "Cancellation requested by System Member to Settlement System Administrator to indicate that the cancellation request must not be forwarded further in the chain.",
  FRAD: "Cancellation requested following a transaction that was originated fraudulently. The use of the FraudulentOrigin code should be governed by jurisdictions.",
  TECH: "Cancellation requested following technical problems resulting in an erroneous transaction.",
  COVR: "Cover payments has either been returned or cancelled.",
  CURR: "Currency of the payment is incorrect.",
  AC02: "Debtor account number invalid or missing.",
  BE16: "Debtor or Ultimate Debtor identification code missing or invalid.",
  FRTR: "Direct Debit Tracking recalled as Mandate Cancelled ",
  DRTP: "Duplication of a request-to-pay message.",
  DT01: "Invalid date (for example, wrong or missing settlement date).",
  DUPL: "Payment is a duplicate of another payment.",
  UPAY: "Payment is not justified.",
  BIAS: "Process a cancellation request but batch already settled.",
  INCR: "Process a cancellation request with incorrect reference to original batch.",
  NARR: "Reason is provided as narrative information in the additional reason information.",
  MD06: "Return of funds requested by end customer.",
  UAPA: "The Debtor is requesting a return of the payment because the payment was not properly authorized. Usage: This code can be used in the case where a Credit Transfer was made without proper authorization from the Debtor. This could be due to compromised end user credentials.",
  SVNR: "The payment is cancelled since a cash amount rendered was not correct or goods or a service was not rendered to the customer, e.g. in an e-commerce situation.",
  PAID: "The underlying transaction in relation to an RTP was already paid (via other means).",
  MODT: "The underlying transaction in relation to an RTP was modified.",
  FRNA: "To complement a rejection response, suggesting the request for cancelation should be forwarded to the next agent in the payment transaction chain.",
  INDM: "To express the wish to establish a bilateral indemnity agreement.",
  AC03: "Wrong account number in Credit Transfer.",
} as const;
export type CANCELLATION_CODES_TYPE = keyof typeof CANCELLATION_CODES;

export const ACWP_REJECT_REASONS = {
  AGNT: "Agent decision",
  AMDT: "Already returned",
  ADAC: "Awaiting debt authorization",
  ARPL: "Awaiting reply",
  AC06: "Blocked account",
  AC04: "Closed account",
  AC07: "Closed creditor account",
  CUST: "Customer decision",
  DUPL: "Duplicate",
  BE01: "Inconsistent end user",
  AM04: "Insufficient funds",
  AC03: "Invalid creditor account",
  BE17: "Invalid creditor identification",
  LEGL: "Legal decision",
  BE04: "Missing incorrect creditor address",
  NARR: "Narrative",
  NOAS: "No answer from customer",
  NOOR: "No original transaction received",
  PTNA: "Passed to next agent",
  RR04: "Regulatory reason",
  RQDA: "Requesting debit authorization",
  AM09: "Wrong amount",
  RC01: "Bank identifier code specified in the message has an incorrect format",
  RC02: "Bank identifier is invalid or missing",
  RC03: "Debtor bank identifier is invalid or missing",
  RC04: "Creditor bank identifier is invalid or missing",
} as const;
export type ACWP_REJECT_REASONS_TYPE = keyof typeof ACWP_REJECT_REASONS;

export const PAYMENT_STATUSES = {
  pending_validation: "Pending validation",
  accepted: "Accepted",
  rejected: "Rejected",
  rejected_fed: "Rejected (FRB)",
  settled: "Settled",
  posted: "Posted",
  accepted_without_post: "Accepted without posting",
  blocked: "Blocked",
  pending_final_post: "Pending final posting",
  awaiting_response: "Awaiting response",
  posting_failed: "Posting failed",
  manually_resolved: "Manually resolved",
  final_post_failed: "Final post failed",
  rejected_reversed: "Rejected reversed",
  rejected_pending_reversal: "Rejected pending reversal",
} as const;
export type PAYMENT_STATUSES_TYPE = keyof typeof PAYMENT_STATUSES;

export const TERMINAL_STATES = [
  "rejected",
  "rejected_fed",
  "posted",
  "blocked",
  "manually_resolved",
  "posting_failed",
  "final_post_failed",
  "rejected_reversed",
  "pending_final_post",
  "rejected_pending_reversal",
] as const;

export const CASE_TYPES = {
  ACWP: "Accepted without posting",
  information_request: "Information request",
  return: "Return request",
} as const;
export type CASE_TYPES_TYPE = keyof typeof CASE_TYPES;

export const PAYMENT_EVENT_TYPE_MAP = {
  actc: "Accepted",
  acsc: "Settled",
  acwp: "Accepted without posting",
  accc: "Posted to ultimate creditor",
  posted: "Posted to core",
  rejected: "Rejected",
  blocked: "Blocked",
  return_requested: "Return requested",
  return_rejected: "Return request rejected",
  return_posted: "Reversal complete",
  return_pending_post: "Reversed on core",
  returned_in_full: "Returned in full",
  partial_return: "Partially returned",
  return_req_exp: "Return request expired",
  payment_final_post: "Posted to ledger",
  manually_resolved: "Manually resolved",
  final_post_failed: "Final ledger posting failed",
  baas_withdrawal_complete: "Ledger withdrawal complete",
  post_failed_retry: "Posting failed (retrying)",
  post_failed_last: "Posting failed (final)",
  sent: "Payment submitted to FedNow",
  acwp_pending: "Pending response received",
} as const;
export type PAYMENT_EVENT_TYPE_MAP_TYPE = keyof typeof PAYMENT_EVENT_TYPE_MAP;

// Subset of ReturnReasonCode enum
export const RETURN_ACCEPT_REASONS = {
  AC15: "Account details changed",
  AC17: "Account in liquidation",
  AC16: "Account sequestered",
  ARDT: "Already returned",
  BE08: "Bank error",
  AC04: "Closed account",
  DUPL: "Duplicate",
  FR01: "Fraud",
  BE01: "Inconsistent with end customer",
  AC01: "Incorrect account",
  FF07: "Invalid purpose",
  SL14: "Maximum debit amount exceeded",
  MD02: "Missing mandatory information in mandate",
  NARR: "Narrative",
  SP02: "Previously stopped",
  RR04: "Regulatory",
  CUST: "Requested by customer",
  AM09: "Wrong amount",
} as const;
export type RETURN_ACCEPT_REASONS_TYPE = keyof typeof RETURN_ACCEPT_REASONS;

export const RETURN_REJECT_REASONS = {
  AGNT: "Agent decision",
  ACLR: "Already cancelled rtp",
  AEXR: "Already expired rtp",
  ARFR: "Already refused rtp",
  ARJR: "Already rejected rtp",
  ARDT: "Already returned",
  ADAC: "Awaiting debit auth",
  ARPL: "Awaiting reply",
  AC04: "Closed account",
  CUST: "Customer decision",
  IDMN: "Indemnity required",
  AM04: "Insufficient funds",
  LEGL: "Legal decision",
  NARR: "Narrative",
  NOAS: "No answer from customer",
  NOOR: "No original transaction",
  PTNA: "Passed to next agent",
  PATE: "Payment already transmitted",
  RR04: "Regulatory",
  RQDA: "Requesting debit authority",
  WSEQ: "Wrong sequence",
} as const;
export type RETURN_REJECT_REASONS_TYPE = keyof typeof RETURN_REJECT_REASONS;

export const ACWP_REASONS = {
  NAME: "Name on account does not match payment.",
} as const;
export type ACWP_REASONS_TYPE = keyof typeof ACWP_REASONS;

export const CASE_RESOLUTIONS = {
  accept: "accepted",
  block: "blocked",
  ipay: "returned in full",
  pecr: "partially returned",
  reject: "rejected",
  expired: "expired",
  manual: "manually closed",
};
export type CASE_RESOLUTIONS_TYPE = keyof typeof CASE_RESOLUTIONS;

export const INFORMATION_REQUEST_RESOLUTIONS = {
  INFO: "Additional information sent",
  NINF: "No further information",
  PDNG: "Pending",
  // IDUP: "Duplicate payment", Not supported
  // IPAY: "Payment initiated" Not supported
};
export type INFORMATION_REQUEST_RESOLUTIONS_TYPE = keyof typeof INFORMATION_REQUEST_RESOLUTIONS;

export const PAYMENT_DIRECTIONS = { receive: "Incoming", send: "Outgoing" } as const;
export type PAYMENT_DIRECTIONS_TYPE = keyof typeof PAYMENT_DIRECTIONS;

export const CASE_STATUSES = { open: "Open", closed: "Closed" };
export type CASE_STATUSES_TYPE = keyof typeof CASE_STATUSES;

export const FEDNOW_TRANSACTION_STATES = {
  pending: "Pending",
  accepted: "Accepted",
  approved: "Approved",
  declined: "Declined",
  failed: "Failed",
  processed: "Processed",
  overridden: "Decline overridden",
  canceled: "Canceled",
  awaiting_approval: "Awaiting dual approval",
  rejected_approval: "Dual approval rejected",
  approval_expired: "Dual approval expired",
  awaiting_bulk_approval: "Awaiting Bulk Approval",
  rejected_bulk_approval: "Rejected bulk approval",
  processed_settled: "Processed and settled",
  processed_reversed: "Processed and reversed",
  overridden_settled: "Processed and settled",
};
export type FEDNOW_TRANSACTION_STATE_TYPE = keyof typeof FEDNOW_TRANSACTION_STATES;
